import { get, post } from 'services/request';
import { ITranslationBank } from 'types/translationBank';
import { ITranslationBankItem, ITranslationBankItemCreate, ITranslationBankItemEdit } from 'types/translationBank';
import { BaseModel } from './BaseModel';
import { TranslationBankModel } from './TranslationBankModel';

export interface ITranslationBankItemsModelOptions {
  render: () => void;
  bankId: number;
  bankModel: TranslationBankModel;
}

export class TranslationBankItemsModel extends BaseModel<ITranslationBankItem, ITranslationBankItemEdit> {
  bankId: number;
  bankModel: TranslationBankModel;

  constructor(options: ITranslationBankItemsModelOptions) {
    super(options);
    this.gridName = 'TranslationBankItems';
    this.bankId = options.bankId;
    this.bankModel = options.bankModel;
    this.getGridColumns();
  }

  async getGrid() {
    await super.getGrid();
    this.gridData = await get<ITranslationBankItem[]>(`/translation-banks/${this.bankId}/items`);
    this.loadingGrid = false;
    this.render();
  }

  async getGridRow(itemId: number) {
    return await get<ITranslationBankItem>(`/translation-bank-items/${itemId}/grid-row`);
  }

  async reload() {
    await super.reload();
    await this.getGrid();
  }

  async createItemFromScratch() {
    this.loadingItem = true;
    this.render();

    const data: ITranslationBankItemCreate = {
      method: 'scratch',
    };
    const result = await post<{ id: number }>(`/translation-banks/${this.bankId}/items`, data);

    if (result.ok && result.data) {
      await this.updateGridRow(result.data.id, 'add');
      await this.bankModel.updateGridRow(this.bankId, 'update');
    }

    this.loadingItem = false;
    this.render();

    return result;
  }

  async createNewItem() {
    return await this.createItemFromScratch();
  }

  async createItemFromTranslation(translationId: number) {
    this.loadingItem = true;
    this.render();

    const data: ITranslationBankItemCreate = {
      method: 'copy',
      translationId,
    };
    const result = await post<{ id: number }>(`/translation-banks/${this.bankId}/items`, data);

    if (result.ok && result.data) {
      await this.updateGridRow(result.data.id, 'add');
      await this.bankModel.updateGridRow(this.bankId, 'update');
    }

    this.loadingItem = false;
    this.render();

    return result;
  }

  async loadItemData(itemId: number) {
    return await get<ITranslationBankItemEdit>(`/translation-bank-items/${itemId}`);
  }

  async moveItem(itemId: number, targetBankId: number) {
    this.loadingItem = true;
    this.render();

    const result = await post<{ id: number; sourceBankId: number; targetBankId: number }>(
      `/translation-bank-items/${itemId}/move`,
      { targetBankId },
    );

    if (result.ok) {
      await Promise.all([this.getGrid(), this.bankModel.updateGridRow(this.bankId, 'update')]);
      // Also refresh target bank in the parent grid
      await this.bankModel.updateGridRow(targetBankId, 'update');
    }

    this.loadingItem = false;
    this.render();

    return result;
  }

  async syncBank(): Promise<{ created: number; deleted: number; skipped: number } | null> {
    const result = await post<{ created: number; deleted: number; skipped: number }>(
      `/translation-banks/${this.bankId}/sync`,
      {},
    );
    if (result.ok && result.data) {
      return result.data;
    }
    return null;
  }

  async getAvailableBanks(): Promise<ITranslationBank[]> {
    const bankModel = this.bankModel;
    // Return banks from the parent grid that have the same agent type and are not the current bank
    const currentBank = bankModel.gridData.find((b) => b.id === this.bankId);
    if (!currentBank) return [];
    return bankModel.gridData.filter((b) => b.id !== this.bankId && b.agentType === currentBank.agentType);
  }
}
